import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  Minus,
  Plus,
  Search,
  Send,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { formatUsd } from '@/lib/format';
import { useCatalog } from '@/hooks/useCatalog';
import { priceFor, pricingPending, useCustomerPricing } from '@/hooks/useCustomerPricing';
import { isPickupItem } from '@/types/domain';
import {
  WILL_CALL_WINDOW,
  earliestWillCallDate,
  fmtPickupDate,
  validateWillCallDate,
} from '@/lib/willCall';

/**
 * /orders/:order_id/change — "Change this order".
 *
 * ⚠ A change REPLACES the order. Service Fusion cannot edit or delete a job
 * through its API (live spec 2026-09-03: 27 endpoints, GET-only except POST on
 * /customers, /jobs and /estimates; `/jobs/{job-id}` has no PUT, PATCH or
 * DELETE). The first cut of this feature emailed ops a diff to apply by hand,
 * which left OUR record permanently stale — the order and its lines still
 * described the original, so the customer's history, Reorder and the estimated
 * total were all wrong and none of it matched the invoice that arrived. So
 * this posts a real replacement order through submit-order with
 * `supersedes_order_id`, and dispatch is asked to delete the old job.
 *
 * ⚠ STILL NOT THE SHOPPING CART, even though it now creates an order. The
 * cart is one global localStorage cart whose Submit builds a fresh order:
 * routing a change through it would clobber whatever the customer already had
 * in there, and leave Review-&-submit one click from a SECOND job with no
 * supersede link. This page holds its own state and has exactly one submit
 * path, which always carries the link.
 *
 * ⚠ It does not compute the diff. The rows show "was N" from the original
 * lines this page loaded; the AUTHORITATIVE change list is the one the server
 * emails dispatch, and it is derived there from the DB's own lines. One diff
 * implementation, and the customer is shown the same replacement order the
 * warehouse will fill.
 */

interface OrderRow {
  id: string;
  order_number: string | null;
  status: string;
  sf_job_id: string | null;
  requested_delivery_date: string | null;
  po_number: string | null;
  /* The replacement inherits all three: a change to an order keeps the same
     ship-to, the same PO and the same delivery-or-pickup mode. */
  customer_id: string;
  customer_location_id: string;
  fulfillment: string | null;
  superseded_by_order_id: string | null;
  /* ⚠ `status` alone does NOT say whether an order is finished — nothing ever
     writes 'delivered', so a picked-up, invoiced order still reads
     'submitted'. Completion lives in these three. See
     _lib/order-supersede.ts. */
  sf_job_status: string | null;
  delivered_at: string | null;
  qbo_invoice_id: string | null;
}

interface LineRow {
  catalog_item_id: string | null;
  item_name: string | null;
  sku: string | null;
  quantity: number;
  line_number: number | null;
}

/**
 * Mirrors _lib/order-supersede.ts so the page can refuse early. The server
 * applies the same rule, so this only saves a round trip.
 *
 * ⚠ SUPERSEDABLE is exactly order-lifecycle-check's Service Fusion watch list,
 * which is what makes the old job's deletion VERIFIABLE rather than merely
 * requested. `out_for_delivery` is excluded on purpose: a fresh job for a
 * loaded truck reads as a second delivery, so those go to the phone.
 *
 * ⚠ And `status` is NOT the whole test. Nothing writes 'delivered' — 153 of
 * 200 live orders are finished and still say 'submitted' — so a delivered or
 * invoiced order has to be caught by `delivered_at` / `qbo_invoice_id` / a
 * terminal `sf_job_status`, or this page would offer to "change" an order that
 * shipped weeks ago.
 */
const SUPERSEDABLE = new Set(['submitted', 'accepted', 'scheduled']);
const TERMINAL_SF_STATUSES = new Set(['completed', 'closed', 'cancelled', 'invoiced', 'done']);

function fmtDate(iso: string): string {
  try {
    const d = iso.includes('T') ? new Date(iso) : new Date(`${iso}T12:00:00`);
    return d.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      timeZone: 'America/Los_Angeles',
    });
  } catch {
    return iso;
  }
}

export default function OrderChange() {
  const params = useParams<{ order_id?: string }>();
  const orderId = params.order_id ?? null;
  const navigate = useNavigate();

  const { data: catalog = [] } = useCatalog();
  const { data: pricing } = useCustomerPricing();
  const pricePending = pricingPending(pricing);

  const [order, setOrder] = useState<OrderRow | null>(null);
  const [original, setOriginal] = useState<LineRow[]>([]);
  const [loading, setLoading] = useState(true);

  // Our OWN quantity state — never the shopping cart.
  const [qtyById, setQtyById] = useState<Record<string, number>>({});
  const [note, setNote] = useState('');
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* ⚠ Distinct from a genuine miss: a failed read used to render as "we
     couldn't find that order", which is what made the `order:<uuid>`
     composite-key bug read as a missing order rather than a bad request. */
  const [loadError, setLoadError] = useState<string | null>(null);
  /** Pickup date for a will-call order. The replacement is a NEW job, so its
   *  date has to be valid TODAY — the original may have gone past the noon
   *  cutoff while the customer was thinking about it. Seeded from the order and
   *  moved forward only when the original no longer passes, with the move
   *  stated on screen rather than done quietly. */
  const [pickupDate, setPickupDate] = useState('');
  const [done, setDone] = useState<{
    orderId: string;
    orderNumber: string | null;
    oldJobId: string | null;
    dispatchEmailed: boolean;
    linked: boolean;
  } | null>(null);

  useEffect(() => {
    if (!orderId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    (async () => {
      const [oRes, lRes] = await Promise.all([
        supabase
          .schema('orders')
          .from('orders')
          .select(
            'id, order_number, status, sf_job_id, requested_delivery_date, po_number, customer_id, customer_location_id, fulfillment, superseded_by_order_id, sf_job_status, delivered_at, qbo_invoice_id',
          )
          .eq('id', orderId)
          .single(),
        supabase
          .schema('orders')
          .from('order_lines')
          .select('catalog_item_id, item_name, sku, quantity, line_number')
          .eq('order_id', orderId)
          .order('line_number'),
      ]);
      if (oRes.data) {
        setOrder(oRes.data as OrderRow);
        setLoadError(null);
      } else if (oRes.error && oRes.error.code !== 'PGRST116') {
        // eslint-disable-next-line no-console
        console.error('[order-change] load failed:', oRes.error);
        setLoadError(oRes.error.message || 'We could not load that order.');
      }
      const lines = (lRes.data as LineRow[] | null) ?? [];
      setOriginal(lines);
      // Seed from the order. ⚠ SUM duplicates, matching the server: an order
      // can carry one catalog item on two lines, and taking the last would
      // show the customer less than is actually on their delivery.
      const seed: Record<string, number> = {};
      for (const l of lines) {
        if (!l.catalog_item_id) continue;
        const q = Math.max(0, Math.floor(Number(l.quantity ?? 0)));
        if (q <= 0) continue;
        seed[l.catalog_item_id] = (seed[l.catalog_item_id] ?? 0) + q;
      }
      setQtyById(seed);
      const o = oRes.data as OrderRow | null;
      if (o?.fulfillment === 'will_call') {
        const wanted = o.requested_delivery_date ?? '';
        setPickupDate(
          wanted && validateWillCallDate(wanted).ok ? wanted : earliestWillCallDate(),
        );
      }
      setLoading(false);
    })();
  }, [orderId]);

  /** Original quantity per catalog item — the "was N" on each row. */
  const originalQty = useMemo(() => {
    const m = new Map<string, number>();
    for (const l of original) {
      if (!l.catalog_item_id) continue;
      const q = Math.max(0, Math.floor(Number(l.quantity ?? 0)));
      if (q <= 0) continue;
      m.set(l.catalog_item_id, (m.get(l.catalog_item_id) ?? 0) + q);
    }
    return m;
  }, [original]);

  /** Wording as it appears on the ORDER, falling back to the catalog. */
  const nameById = useMemo(() => {
    const m = new Map<string, { name: string; sku: string | null }>();
    for (const c of catalog) {
      m.set(c.id, { name: c.displayName || c.name, sku: c.sku });
    }
    for (const l of original) {
      if (!l.catalog_item_id) continue;
      m.set(l.catalog_item_id, {
        name: l.item_name?.trim() || m.get(l.catalog_item_id)?.name || 'Item',
        sku: l.sku?.trim() || m.get(l.catalog_item_id)?.sku || null,
      });
    }
    return m;
  }, [catalog, original]);

  const catalogById = useMemo(() => {
    const m = new Map(catalog.map((c) => [c.id, c]));
    return m;
  }, [catalog]);

  /** Every item involved: still on the order, or added. Removed rows stay
   *  visible at 0 so the customer can put one back without re-finding it. */
  const rowIds = useMemo(() => {
    const ids = new Set<string>([...originalQty.keys(), ...Object.keys(qtyById)]);
    return [...ids].sort((a, b) => {
      const an = nameById.get(a)?.name ?? '';
      const bn = nameById.get(b)?.name ?? '';
      return an.localeCompare(bn);
    });
  }, [originalQty, qtyById, nameById]);

  const changedCount = useMemo(() => {
    let n = 0;
    const ids = new Set<string>([...originalQty.keys(), ...Object.keys(qtyById)]);
    for (const id of ids) {
      if ((originalQty.get(id) ?? 0) !== (qtyById[id] ?? 0)) n += 1;
    }
    return n;
  }, [originalQty, qtyById]);

  const revisedTotal = useMemo(() => {
    if (pricePending) return null;
    let t = 0;
    for (const [id, q] of Object.entries(qtyById)) {
      if (q <= 0) continue;
      const item = catalogById.get(id);
      if (!item) continue;
      t += q * priceFor(item, pricing).unitPrice;
    }
    return t;
  }, [qtyById, catalogById, pricing, pricePending]);

  const searchResults = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (term.length < 2) return [];
    return catalog
      .filter(
        (c) =>
          c.orderable &&
          c.active &&
          !isPickupItem(c) &&
          !(qtyById[c.id] ?? 0) &&
          ((c.displayName || c.name).toLowerCase().includes(term) ||
            (c.sku ?? '').toLowerCase().includes(term)),
      )
      .slice(0, 8);
  }, [search, catalog, qtyById]);

  function setQty(id: string, q: number) {
    setQtyById((prev) => {
      const next = { ...prev };
      const clamped = Math.max(0, Math.floor(Number.isFinite(q) ? q : 0));
      if (clamped === 0) delete next[id];
      else next[id] = clamped;
      return next;
    });
  }

  function adjust(id: string, delta: number) {
    setQty(id, (qtyById[id] ?? 0) + delta);
  }

  const isWillCall = order?.fulfillment === 'will_call';
  const pickupCheck = isWillCall ? validateWillCallDate(pickupDate) : null;
  const pickupProblem = pickupCheck && !pickupCheck.ok ? pickupCheck.error : null;
  const pickupMoved = Boolean(
    isWillCall &&
      order?.requested_delivery_date &&
      pickupDate &&
      pickupDate !== order.requested_delivery_date,
  );

  /* ⚠ A replacement with the SAME contents is not a change — it would create a
     second job and a second invoice for nothing. So unlike the old
     diff-email flow, a bare note is NOT enough on its own: a note with no
     quantity change is a message, and the place for that is the phone. */
  const canSubmit =
    !submitting && changedCount > 0 && !pickupProblem && Object.keys(qtyById).length > 0;

  async function submit() {
    if (!orderId || !order || !canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      if (!token) throw new Error('Not signed in.');

      /* The replacement goes through the NORMAL customer-authenticated
         submit-order path — not the internal-token path Chloe and the EDI
         intake use, which sets submitted_by to null and would change who gets
         the confirmation. Everything else (contract pricing, fees, the SF job,
         the confirmation email) is the same code that placed the original. */
      const res = await fetch('/.netlify/functions/submit-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          customer_id: order.customer_id,
          customer_location_id: order.customer_location_id,
          lines: Object.entries(qtyById)
            .filter(([, q]) => q > 0)
            .map(([catalog_item_id, quantity]) => ({ catalog_item_id, quantity })),
          po_number: order.po_number ?? undefined,
          delivery_notes: note.trim() ? note.trim() : undefined,
          fulfillment: isWillCall ? 'will_call' : 'delivery',
          requested_delivery_date: isWillCall
            ? pickupDate
            : (order.requested_delivery_date ?? undefined),
          /* ⚠ submit-order's `dry_run` DEFAULTS TO TRUE — a caller that omits
             it gets a priced preview, HTTP 200 `ok:true`, and no order at all.
             Every other caller (the cart, the Order Desk, EDI, the voice brain)
             sends `false` explicitly; this page did not, so every change ever
             attempted was silently a preview: no order row, no SF job, and then
             a thrown error because a preview carries no `order_id`. That is the
             whole of the "order didn't go through" report on SF 1096522891. */
          dry_run: false,
          supersedes_order_id: orderId,
          // Same fingerprint rule as the cart: a double-tap collapses onto one
          // order rather than creating a second replacement.
          idempotency_key: `change:${orderId}:${JSON.stringify(qtyById)}`,
        }),
      });
      /* ⚠ Read the body as TEXT first. `res.json().catch(() => ({}))` turned a
         platform error page (a function crash or a blown timeout returns HTML,
         not JSON) into `{}`, so the only thing left to show was the bare "That
         did not go through" — which says nothing anyone can act on and hides
         the one fact that identifies the failure. Fourth time this same swallow
         has cost a debugging session; the status code alone would have named it. */
      const raw = await res.text();
      let json: {
        ok?: boolean;
        error?: string;
        order_id?: string;
        order_number?: string | null;
        supersede?: { linked?: boolean; notified?: boolean };
      } = {};
      let parsed = true;
      try {
        json = raw ? JSON.parse(raw) : {};
      } catch {
        parsed = false;
      }
      if (!res.ok || !json.ok || !json.order_id) {
        // eslint-disable-next-line no-console
        console.error('[order-change] submit failed', {
          status: res.status,
          parsed,
          body: raw.slice(0, 2000),
        });
        if (json.error) throw new Error(json.error);
        if ((json as { dry_run?: boolean }).dry_run) {
          // Nothing was placed. Never let this read as an ordinary failure
          // again — the whole point is that it looks like success.
          throw new Error(
            'We only priced this change instead of placing it — nothing was ordered. Please tell us so we can fix it.',
          );
        }
        /* ⚠ A non-JSON body at this point means the SF job may ALREADY EXIST —
           submit-order posts to Service Fusion before it writes anything local,
           so a crash or timeout after that leaves a job with no order behind it.
           Never tell someone to "try again" here: a retry would place a second
           job. The idempotency key makes an identical retry safe, but the person
           reading this cannot know that, so send them to us instead. */
        throw new Error(
          !parsed || !raw
            ? `The server didn't answer properly (HTTP ${res.status}). Your change may or may not have reached us — please call before trying again, so you don't end up with two deliveries.`
            : `That did not go through (HTTP ${res.status}). ${raw.slice(0, 300)}`,
        );
      }
      setDone({
        orderId: json.order_id,
        orderNumber: json.order_number ?? null,
        oldJobId: order.sf_job_id,
        dispatchEmailed: Boolean(json.supersede?.notified),
        linked: Boolean(json.supersede?.linked),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not go through.');
    } finally {
      setSubmitting(false);
    }
  }

  // ---- states -------------------------------------------------------------

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="flex items-center gap-2 text-sm text-brix-muted">
          <Loader2 size={16} className="animate-spin" /> Loading order…
        </div>
      </div>
    );
  }

  if (!orderId || !order) {
    return (
      <div className="h-full flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <AlertTriangle size={26} className="mx-auto mb-3 text-brix-muted" />
          <p className="text-sm text-brix-muted">
            {loadError ? `We couldn't load that order — ${loadError}` : "We couldn't find that order."}{' '}
            <Link to="/order" className="text-brix-primary font-semibold underline">
              Back to orders
            </Link>
          </p>
        </div>
      </div>
    );
  }

  const orderLabel = order.order_number ?? `Order ${order.id.slice(0, 8)}`;

  /* Refuse early, and say which case it is. ⚠ An order that has ALREADY been
     replaced is refused too: changing it again would chain a third job onto a
     stale one and leave dispatch two jobs to delete. The server refuses this
     as well — the page just says so without a round trip. */
  const blocked = !SUPERSEDABLE.has(order.status)
    ? order.status === 'out_for_delivery'
      ? "It's already out for delivery — please give us a call and we'll sort it out with the driver."
      : order.status === 'delivered'
        ? 'It has already been delivered — give us a call and we will sort it out.'
        : order.status === 'cancelled'
          ? 'It was cancelled, so there is nothing to change.'
          : 'It has already been invoiced, so it needs a billing correction rather than a change. Give us a call.'
    : order.superseded_by_order_id
      ? 'This order has already been replaced by a newer one — open that order and change it instead.'
      : order.qbo_invoice_id
        ? 'It has already been invoiced, so it needs a billing correction rather than a change. Give us a call.'
        : order.delivered_at
          ? 'It has already been completed — give us a call and we will sort it out.'
          : TERMINAL_SF_STATUSES.has((order.sf_job_status ?? '').trim().toLowerCase())
            ? 'It is already closed out on our side. Give us a call and we will sort it out.'
            : null;

  if (blocked) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-10">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-center">
          <AlertTriangle size={22} className="mx-auto mb-2 text-amber-700" />
          <h1 className="text-sm font-semibold text-amber-900">
            {orderLabel} can no longer be changed here.
          </h1>
          <p className="text-[13px] text-amber-800 mt-1">{blocked}</p>
          <Button variant="secondary" className="mt-4" onClick={() => navigate(`/orders/${order.id}`)}>
            Back to the order
          </Button>
        </div>
      </div>
    );
  }

  // ---- submitted ----------------------------------------------------------

  if (done) {
    const newLabel = done.orderNumber ?? 'your new order';
    return (
      <div className="max-w-2xl mx-auto px-4 py-10">
        <div className="rounded-2xl border border-brix-primary/30 bg-brix-primary-tint/50 p-6">
          <div className="flex items-start gap-3">
            <CheckCircle2 size={22} className="text-brix-primary shrink-0 mt-0.5" />
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-brix-ink">Order changed</h1>
              <p className="text-sm text-brix-muted mt-1">
                {newLabel} replaces {orderLabel} — same delivery, updated contents. It's confirmed
                and on its way to our warehouse, and your confirmation email covers the new order.
              </p>
            </div>
          </div>

          {/* ⚠ The old Service Fusion job is the one thing we cannot close
              ourselves, so the page says who is doing it rather than implying
              it is done. `dispatchEmailed` false is a DIFFERENT outcome from
              "we told them" and the customer is told to call — the change
              itself has landed either way. */}
          <div
            className={cn(
              'mt-5 rounded-xl border px-4 py-3 text-[13px] leading-relaxed',
              done.dispatchEmailed
                ? 'border-brix-border bg-brix-surface text-brix-muted'
                : 'border-amber-200 bg-amber-50 text-amber-900',
            )}
          >
            {done.dispatchEmailed ? (
              <>
                We've asked our team to cancel the earlier version
                {done.oldJobId ? ` (order #${done.oldJobId})` : ''} so you only get one delivery.
                Nothing else is needed from you.
              </>
            ) : (
              <>
                <span className="font-semibold">Please give us a call.</span> Your new order is
                confirmed, but we couldn't notify the team to cancel the earlier version
                {done.oldJobId ? ` (order #${done.oldJobId})` : ''} — a quick call makes sure you
                aren't sent both.
              </>
            )}
          </div>

          <div className="flex gap-2 mt-5">
            <Button onClick={() => navigate(`/orders/${done.orderId}`)}>
              View {done.orderNumber ? done.orderNumber : 'the new order'}
            </Button>
            <Button variant="secondary" onClick={() => navigate('/order')}>
              All orders
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ---- editor -------------------------------------------------------------

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <Link
        to={`/orders/${order.id}`}
        className="inline-flex items-center gap-1.5 text-[13px] text-brix-muted hover:text-brix-primary mb-4"
      >
        <ArrowLeft size={14} /> Back to the order
      </Link>

      <h1 className="text-2xl font-bold text-brix-ink tracking-tight">Change this order</h1>
      <p className="text-sm text-brix-muted mt-1.5 max-w-xl">
        Adjust what you need on {orderLabel}
        {order.sf_job_id ? ` (order #${order.sf_job_id})` : ''}
        {!isWillCall && order.requested_delivery_date
          ? `, scheduled for ${fmtDate(order.requested_delivery_date)}`
          : ''}
        . When you send this we'll place the corrected version and cancel the
        earlier one, so you still get one {isWillCall ? 'pickup' : 'delivery'} —
        same {isWillCall ? 'pickup day' : 'day'}, same address.
      </p>

      {isWillCall && (
        <div className="mt-5 rounded-xl border border-brix-border bg-brix-surface shadow-card p-4">
          <label htmlFor="change-pickup" className="text-sm font-semibold text-brix-ink">
            Pickup day
          </label>
          <p className="text-[11px] text-brix-muted mt-0.5 mb-2">
            Will-call hours are {WILL_CALL_WINDOW}.
          </p>
          <input
            id="change-pickup"
            type="date"
            value={pickupDate}
            min={earliestWillCallDate()}
            onChange={(e) => setPickupDate(e.target.value)}
            data-testid="pickup-date"
            className="text-sm rounded-lg border border-brix-border bg-brix-surface px-3 py-2 outline-none focus-visible:border-brix-primary"
          />
          {pickupProblem ? (
            <p role="alert" className="mt-2 text-[12px] font-semibold text-red-700">
              {pickupProblem}
            </p>
          ) : pickupMoved ? (
            /* ⚠ Said out loud. The replacement is a NEW job, so if the original
               pickup day has passed the cutoff it cannot be reused — moving it
               quietly would change the customer's plans without telling them. */
            <p className="mt-2 text-[12px] text-amber-800">
              The original day is no longer bookable, so we've moved this to{' '}
              <span className="font-semibold">{fmtPickupDate(pickupDate)}</span>. Pick another day
              if that doesn't work.
            </p>
          ) : null}
        </div>
      )}

      {/* Lines */}
      <ul className="mt-6 space-y-2">
        {rowIds.map((id) => {
          const meta = nameById.get(id);
          const item = catalogById.get(id);
          const was = originalQty.get(id) ?? 0;
          const now = qtyById[id] ?? 0;
          const isNew = was === 0;
          const isGone = now === 0 && was > 0;
          const isChanged = !isNew && !isGone && now !== was;
          const unitPrice = item ? priceFor(item, pricing).unitPrice : null;
          return (
            <li
              key={id}
              data-testid={`change-row-${id}`}
              className={cn(
                'rounded-xl border bg-brix-surface shadow-card p-3',
                isGone
                  ? 'border-red-200 bg-red-50/50'
                  : isNew
                    ? 'border-emerald-200 bg-emerald-50/40'
                    : isChanged
                      ? 'border-amber-200 bg-amber-50/40'
                      : 'border-brix-border',
              )}
            >
              <div className="flex items-start gap-3">
                <div
                  className="w-2 h-10 rounded-sm shrink-0 mt-0.5"
                  style={{ background: item?.accentColor ?? '#6A7780' }}
                  aria-hidden
                />
                <div className="flex-1 min-w-0">
                  <p
                    className={cn(
                      'text-sm font-semibold truncate',
                      isGone ? 'text-brix-muted line-through' : 'text-brix-ink',
                    )}
                  >
                    {meta?.name ?? 'Item'}
                  </p>
                  <p className="text-[11px] text-brix-muted">
                    {meta?.sku ? <span className="font-mono">{meta.sku}</span> : '—'}
                    {unitPrice != null && !pricePending && (
                      <> · {formatUsd(unitPrice)} each</>
                    )}
                  </p>
                  <div className="mt-1 text-[11px] font-semibold">
                    {isGone && <span className="text-red-700">Removing — was {was}</span>}
                    {isNew && <span className="text-emerald-700">Adding {now}</span>}
                    {isChanged && (
                      <span className="text-amber-700">
                        Changing — was {was}, now {now}
                      </span>
                    )}
                    {!isGone && !isNew && !isChanged && (
                      <span className="text-brix-muted">No change · {was}</span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => adjust(id, -1)}
                    aria-label={`Decrease ${meta?.name ?? 'item'}`}
                    className="w-7 h-7 rounded-md border border-brix-primary text-brix-primary flex items-center justify-center hover:bg-brix-primary-tint cursor-pointer transition-colors"
                  >
                    <Minus size={12} />
                  </button>
                  <input
                    type="number"
                    min={0}
                    value={now}
                    onChange={(e) => setQty(id, parseInt(e.target.value || '0', 10))}
                    aria-label={`${meta?.name ?? 'Item'} quantity`}
                    data-testid={`qty-${id}`}
                    className="w-14 text-center font-mono font-bold text-sm outline-none border border-brix-border rounded-md py-1 focus-visible:border-brix-primary"
                  />
                  <button
                    type="button"
                    onClick={() => adjust(id, 1)}
                    aria-label={`Increase ${meta?.name ?? 'item'}`}
                    className="w-7 h-7 rounded-md bg-brix-primary text-white flex items-center justify-center hover:opacity-90 cursor-pointer transition-colors"
                  >
                    <Plus size={12} />
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {/* Add an item */}
      <div className="mt-5 rounded-xl border border-brix-border bg-brix-surface shadow-card p-4">
        <p className="text-sm font-semibold text-brix-ink mb-2">Add something else</p>
        <div className="relative">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-brix-muted pointer-events-none"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products by name or SKU…"
            aria-label="Search products to add"
            className="pl-9 bg-brix-surface"
          />
        </div>
        {search.trim().length >= 2 && (
          <ul className="mt-2 divide-y divide-brix-border rounded-lg border border-brix-border overflow-hidden">
            {searchResults.length === 0 ? (
              <li className="px-3 py-2.5 text-[13px] text-brix-muted">
                Nothing matches “{search.trim()}”.
              </li>
            ) : (
              searchResults.map((c) => (
                <li key={c.id} className="flex items-center gap-3 px-3 py-2.5">
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-semibold text-brix-ink truncate">
                      {c.displayName || c.name}
                    </p>
                    <p className="text-[11px] text-brix-muted font-mono">{c.sku ?? '—'}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setQty(c.id, 1);
                      setSearch('');
                    }}
                  >
                    <Plus size={12} /> Add
                  </Button>
                </li>
              ))
            )}
          </ul>
        )}
      </div>

      {/* Note */}
      <div className="mt-5 rounded-xl border border-brix-border bg-brix-surface shadow-card p-4">
        <label htmlFor="change-note" className="text-sm font-semibold text-brix-ink">
          Anything else we should know?
        </label>
        <p className="text-[11px] text-brix-muted mt-0.5 mb-2">
          A time window, where to leave it — whatever our driver should know. This
          rides on the new order. ⚠ To change only the day or the notes and nothing
          else, give us a call: a replacement order with the same contents would
          just be a second invoice.
        </p>
        <textarea
          id="change-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          maxLength={4000}
          placeholder="e.g. Please deliver Thursday instead of Wednesday."
          className="w-full text-sm rounded-lg border border-brix-border bg-brix-surface px-3 py-2 outline-none focus-visible:border-brix-primary resize-y"
        />
      </div>

      {/* Summary + submit */}
      <div className="mt-5 rounded-2xl border-2 border-brix-primary/90 bg-brix-surface/85 shadow-card-deep overflow-hidden">
        <div className="h-1.5 bg-brix-primary" aria-hidden />
        <div className="p-4 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm text-brix-ink">
              {changedCount === 0
                ? 'No changes yet'
                : `${changedCount} ${changedCount === 1 ? 'change' : 'changes'}`}
            </p>
            <p className="text-sm font-mono font-semibold text-brix-ink">
              {revisedTotal == null ? '—' : formatUsd(revisedTotal)}
            </p>
          </div>
          <p className="text-[11px] text-brix-muted">
            Estimated — your contract pricing is applied when we invoice.
          </p>

          {error && (
            <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-800">
              {error}
            </div>
          )}

          <Button className="w-full" disabled={!canSubmit} onClick={submit}>
            {submitting ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Sending…
              </>
            ) : (
              <>
                <Send size={14} /> Replace this order
              </>
            )}
          </Button>
          <p className="text-[11px] text-brix-muted text-center">
            {Object.keys(qtyById).length === 0
              ? 'An order needs at least one item — to cancel entirely, give us a call.'
              : "This replaces the order you're changing. You'll get one confirmation for the new one, and we'll cancel the earlier version."}
          </p>
        </div>
      </div>
    </div>
  );
}
